import {
  SoftSurveyComplete,
  SoftSurveyIntro,
  SoftSurveyQuestion,
} from '@chiroup/components/survey/soft';
import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  SurveyBehavesAs,
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';
import React, { useEffect, useState } from 'react';
import { reportRequestSubmitError } from '../../services/reportError';
import requestService from '../../services/request.service';
import InterCCS from './InterCCS';
import InterReeval from './InterReeval';

type Status = 'intro' | 'question' | 'complete';

// Kiosk only: once the patient finishes their final survey, the "All
// done" panel auto-returns to the login (phone-entry) screen after this
// delay so the next patient isn't left looking at someone else's
// confirmation. They can also tap "Done" to leave immediately.
const KIOSK_ALL_DONE_REDIRECT_MS = 4000;

type Props = {
  requestId: string;
  validationCode: string | number;
  patientSurveyId: string;
  // The survey shape from the legacy bootstrap (`SurveyHealthcom`):
  // `currentQuestion` / `previousQuestion` / `progress` / `started`
  // are precomputed by the server and `questions` is intentionally
  // undefined for non-custom-behavesAs surveys (so the client can't
  // skip ahead). The Cognito-authed flow can also pass a full
  // `questions` array if it wants to drive locally.
  survey: {
    id?: string;
    title?: string;
    subtitle?: string;
    startMessage?: string;
    endMessage?: string;
    questions?: SurveyQuestion[];
    currentQuestion?: SurveyQuestion;
    previousQuestion?: string;
    progress?: SurveyProgress;
    started?: boolean;
    // The CCS / reevaluation flow uses a body picker + chained
    // follow-ups that the soft-card UI can't represent. When this is
    // 'ccs' / 'reevaluation' / 'abbreviatedCCS' we delegate the
    // entire survey to the legacy `<ChiefComplaintSurvey>` via
    // `<InterCCS>`. Otherwise the standard per-question runner.
    behavesAs?: string | null;
    allowAdditionalComplaints?: boolean;
  };
  language: 'en' | 'es';
  primaryColor: string;
  prMap?: { [key: string]: string };
  encounterId?: string;
  kiosk?: boolean;
  // Kiosk single-step mode: `lastStep` is true when this survey is the
  // patient's final outstanding step. On completion the panel then
  // redirects straight to the kiosk login via `onFinishAll` (and shows
  // a "Done" button) instead of routing back to the menu.
  lastStep?: boolean;
  onFinishAll?: () => void;
  remainingSteps?: number[];
  onComplete: () => void;
  alreadyComplete?: boolean;
  // Patient context surfaced by the bootstrap (`requestGetAuth`) and
  // forwarded through `RequestRunner`. CCS needs gender + age to pick
  // the right anatomy / question set.
  gender?: Genders;
  age?: number;
  ccsFollowups?: string[];
  surveyIds?: string[];
  // Patient's existing complaints — required by the reevaluation flow
  // so <ReevalSurvey> can pre-populate per-complaint follow-up
  // questions. Optional because non-reeval surveys don't need it.
  complaints?: any[];
  // Refresh callback forwarded to InterCCS / InterReeval. The
  // whole-survey submit (`PUT /requests/{id}/survey/{patientSurveyId}`)
  // returns a fresh bootstrap with newly-queued body-region followups
  // (NDI, Oswestry, etc.) or the next ACCS in a chain. Without this
  // wire-up, those new steps never reach the parent state — the patient
  // hits "All set" instead of the followup. RequestRunner builds this
  // callback from `onUpdateRequest`.
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
};

/**
 * `/requests/:id` survey step — wraps the same Soft Card panels used by
 * the Cognito-authed runner but drives them with the
 * validationCode-keyed public endpoints (the legacy
 * `requestSurveyAnswerSubmit` / `requestSurveyGetQuestion` Lambdas).
 */
const StepSurvey: React.FC<Props> = ({
  requestId,
  validationCode,
  patientSurveyId,
  survey,
  language,
  primaryColor,
  prMap,
  encounterId,
  kiosk,
  lastStep,
  onFinishAll,
  remainingSteps,
  onComplete,
  alreadyComplete,
  gender,
  age,
  ccsFollowups,
  surveyIds: surveyIdsProp,
  complaints,
  updateRequest,
}) => {
  // Built-in templates arrive without `behavesAs`, so resolve them by
  // well-known id. Custom surveys carry `behavesAs` explicitly.
  // `additionalComplaintSurvey` isn't a `SurveyBehavesAs` value — it's
  // detected separately below by id.
  const idToBehavesAs: Record<string, SurveyBehavesAs> = {
    [surveyIds.ccs]: SurveyBehavesAs.CCS,
    [surveyIds.abbreviatedCCS]: SurveyBehavesAs.AbbreviatedCCS,
    [surveyIds.reevaluation]: SurveyBehavesAs.Reevaluation,
    [surveyIds.interimVisitSurvey]: SurveyBehavesAs.Interim,
  };
  const effectiveBehavesAs: SurveyBehavesAs | null =
    (survey.behavesAs as SurveyBehavesAs | undefined | null) ||
    (survey.id ? idToBehavesAs[survey.id] : undefined) ||
    null;
  const isAdditionalComplaintsSurvey = survey.id === surveyIds.additionalComplaintSurvey;

  // Reeval must route to <InterReeval> (per-complaint followups + body
  // picker on "Yes"), not <InterCCS> which would submit and exit on
  // the single new-problems answer.
  const isReeval = effectiveBehavesAs === SurveyBehavesAs.Reevaluation;
  const ccsLikeBehavesAs =
    effectiveBehavesAs === SurveyBehavesAs.CCS ||
    effectiveBehavesAs === SurveyBehavesAs.AbbreviatedCCS ||
    isAdditionalComplaintsSurvey;
  // Custom CCS-family surveys saved before the body-picker-first
  // invariant may have a non-bodypicker first question; force the
  // default so they don't dead-end after one question.
  const defaultBodyPicker: SurveyQuestion = {
    id: 'e89903d4-5f69-4c64-a3fc-7827c9179514',
    problemArea: 1,
    question: 'Please click on the body to select the parts that are causing you pain.',
    type: 'bodypicker',
  } as SurveyQuestion;
  const rawStart = survey.currentQuestion ?? survey.questions?.[0];
  const startQuestion =
    ccsLikeBehavesAs && rawStart?.type !== 'bodypicker' ? defaultBodyPicker : rawStart;
  // Drive CCS routing strictly off behavesAs / well-known id — NOT off a
  // leading bodypicker. A standard survey can end up with an orphaned body
  // picker as question 1 (e.g. set to CCS then back to standard); routing it
  // as CCS here hijacks the whole survey and skips the custom questions.
  const isCCSLike = !isReeval && ccsLikeBehavesAs;
  // Server pre-computes `currentQuestion` for kiosk + `/requests`
  // bootstraps. If the caller supplied a full `questions[]` (Cognito
  // flow loading the whole survey), fall back to the first
  // unanswered question. Either way, this is the question the
  // runner lands on when the patient hits "Get started".
  const initialQuestions = (survey.questions || []).filter(Boolean);
  const fallbackUnanswered = initialQuestions.find(q => !q.answer) ?? null;
  const initialCurrent = survey.currentQuestion ?? fallbackUnanswered;

  // Mirror the same fallback logic for `previousQuestion`/`progress`.
  // `progress` is what the SoftSurveyQuestion renders on the top pill,
  // so a missing initial value just shows "0 / N" until the first
  // submitAnswer response comes back.
  const initialPrevious = survey.previousQuestion;
  const initialProgress: SurveyProgress | undefined =
    survey.progress ??
    (initialQuestions.length
      ? {
          completed: initialQuestions.filter(q => !!q.answer).length,
          total: initialQuestions.length,
        }
      : undefined);

  // If the patient already started the survey on a different device
  // and we're resuming mid-flow, skip the intro screen.
  const initialStatus: Status = alreadyComplete
    ? 'complete'
    : !initialCurrent
      ? 'complete'
      : survey.started
        ? 'question'
        : 'intro';

  const [status, setStatus] = useState<Status>(initialStatus);
  const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestion | null>(
    initialCurrent ?? null
  );
  const [previousQuestionId, setPreviousQuestionId] = useState<string | undefined>(initialPrevious);
  const [progress, setProgress] = useState<SurveyProgress | undefined>(initialProgress);
  const [submitting, setSubmitting] = useState(false);
  const [hideSteps, setHideSteps] = useState(false);

  // Kiosk + final step: the completion panel becomes the "All done"
  // screen that returns the patient to the login screen, rather than a
  // "Continue" that bounces back to the menu.
  const kioskAllDone = !!(kiosk && lastStep && onFinishAll);

  useEffect(() => {
    if (alreadyComplete) {
      setStatus('complete');
    }
  }, [alreadyComplete]);

  // Auto-return to the kiosk login once the patient lands on the final
  // "All done" panel, so a patient who walks away doesn't leave their
  // confirmation up for the next person. Tapping "Done" does the same
  // thing immediately.
  useEffect(() => {
    if (status !== 'complete' || !kioskAllDone) return;

    const timer = setTimeout(() => onFinishAll?.(), KIOSK_ALL_DONE_REDIRECT_MS);

    return () => clearTimeout(timer);
  }, [status, kioskAllDone, onFinishAll]);

  // Kiosk: hand off to the runner as soon as the survey finishes (no
  // Continue tap; the runner shows the completion checkmark and advances).
  const kioskAutoAdvance = !!kiosk && !kioskAllDone;
  useEffect(() => {
    if (status !== 'complete' || !kioskAutoAdvance) return;

    onComplete();
  }, [status, kioskAutoAdvance, onComplete]);

  const start = () => {
    if (!currentQuestion) {
      setStatus('complete');

      return;
    }

    setHideSteps(true);
    setStatus('question');
    setTimeout(() => setHideSteps(false), 250);
  };

  const onSelect = async (val: SurveyQuestionAnswer) => {
    if (!currentQuestion?.id) return;

    setSubmitting(true);
    setHideSteps(true);

    try {
      const { question: nextQuestion, progress: nextProgress } =
        await requestService.submitSurveyAnswer({
          requestId,
          patientSurveyId,
          patientSurveyQuestionId: currentQuestion.id,
          answer: val,
          validationCode,
          prMap,
          encounterId,
          kiosk,
          remainingSteps,
        });

      setProgress(nextProgress);

      if (nextQuestion?.id) {
        setPreviousQuestionId(currentQuestion.id);
        setCurrentQuestion(nextQuestion);
        // Long surveys leave the patient scrolled past the question
        // text by the time they're picking an answer at the bottom.
        // Snap back to the top so the next question's heading is in
        // view immediately. Same on `goBack` below.
        scrollToTop();
      } else {
        setStatus('complete');
        scrollToTop();
      }
    } catch (err) {
      reportRequestSubmitError(err, { step: 'survey:answer' });
    } finally {
      setSubmitting(false);
      setHideSteps(false);
    }
  };

  const goBack = async () => {
    if (!previousQuestionId) return;

    try {
      const res = await requestService.getPreviousQuestion({
        requestId,
        patientSurveyId,
        questionId: previousQuestionId,
        prMap,
        kiosk,
      });

      setCurrentQuestion(res.question);
      setPreviousQuestionId(res.previousQuestion);
      setProgress(res.progress);
      scrollToTop();
    } catch (err) {
      reportRequestSubmitError(err, { step: 'survey:goBack' });
    }
  };

  const scrollToTop = () => {
    // The runner's outer scroll container is the document itself, but
    // some embeds (kiosk frame, parent app) may scroll a different
    // element. Hit both: window for the page and any scrollable
    // ancestor of the survey question that has overflow.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reeval takes over the entire surface via <ReevalSurvey> (wrapped
  // by InterReeval). It expects the existing patient complaints so it
  // can expand per-complaint follow-up questions before the
  // new-problems prompt. Must come before the CCS branch — a reeval
  // survey could otherwise match `isCCSLike` if the backend ever sent
  // a bodypicker as the start question.
  if (isReeval && startQuestion) {
    return (
      <InterReeval
        requestId={requestId}
        patientSurveyId={patientSurveyId}
        validationCode={validationCode}
        primaryColor={primaryColor}
        language={language}
        gender={gender ?? Genders.notDisclosed}
        age={age ?? 0}
        startQuestion={startQuestion}
        ccsFollowups={ccsFollowups}
        surveyIds={surveyIdsProp}
        prMap={prMap ?? {}}
        encounterId={encounterId}
        kiosk={kiosk}
        complete={onComplete}
        markSurveyComplete={onComplete}
        complaints={complaints ?? []}
        allowAdditionalComplaints={survey.allowAdditionalComplaints !== false}
        customQuestions={
          survey.behavesAs === SurveyBehavesAs.Reevaluation ? survey.questions : undefined
        }
        customCCSSurveyId={
          survey.behavesAs === SurveyBehavesAs.Reevaluation ? survey.id : undefined
        }
        updateRequest={updateRequest}
      />
    );
  }

  // CCS takes over the entire survey surface. Skip the soft intro /
  // per-question dispatcher entirely and hand off to the legacy
  // ChiefComplaintSurvey via InterCCS. It owns its own intro screen,
  // its own progress state, and submits the whole survey through
  // `requestService.submitSurvey`.
  if (isCCSLike && startQuestion) {
    return (
      <InterCCS
        requestId={requestId}
        patientSurveyId={patientSurveyId}
        validationCode={validationCode}
        primaryColor={primaryColor}
        language={language}
        gender={gender ?? Genders.notDisclosed}
        age={age ?? 0}
        startQuestion={startQuestion}
        ccsFollowups={ccsFollowups}
        surveyIds={surveyIdsProp}
        prMap={prMap ?? {}}
        encounterId={encounterId}
        kiosk={kiosk}
        complete={onComplete}
        markSurveyComplete={onComplete}
        additionalComplaints={isAdditionalComplaintsSurvey}
        abbreviatedCCS={effectiveBehavesAs === SurveyBehavesAs.AbbreviatedCCS}
        allowAdditionalComplaints={survey.allowAdditionalComplaints !== false}
        // Only forward question lists for *custom* CCS-family templates.
        // Built-in templates embed only the bodypicker; ChiefComplaintSurvey
        // generates the abbreviated per-region followups itself, and
        // passing the bodypicker-only array would short-circuit that.
        customQuestions={
          survey.behavesAs === SurveyBehavesAs.CCS ||
          survey.behavesAs === SurveyBehavesAs.AbbreviatedCCS
            ? survey.questions
            : undefined
        }
        // Only set for *custom* CCS-family templates (detected via the
        // `behavesAs` column). Built-ins resolve via well-known ids.
        customCCSSurveyId={
          survey.behavesAs === SurveyBehavesAs.CCS ||
          survey.behavesAs === SurveyBehavesAs.AbbreviatedCCS
            ? survey.id
            : undefined
        }
        currentSurveyId={survey.id}
        updateRequest={updateRequest}
      />
    );
  }

  if (status === 'intro') {
    return (
      <SoftSurveyIntro
        meta={{
          title: survey.title ?? '',
          subtitle: survey.subtitle,
          startMessage: survey.startMessage,
        }}
        language={language}
        primaryColor={primaryColor}
        onStart={start}
      />
    );
  }

  if (status === 'question' && currentQuestion) {
    return (
      <SoftSurveyQuestion
        question={currentQuestion}
        progress={progress}
        language={language}
        primaryColor={primaryColor}
        disabled={submitting}
        hidden={hideSteps}
        showPrevious={!!previousQuestionId}
        onBack={goBack}
        onSelect={onSelect}
      />
    );
  }

  // Kiosk: runner shows the completion checkmark; nothing to render here.
  if (kioskAutoAdvance) return null;

  return (
    <SoftSurveyComplete
      language={language}
      primaryColor={primaryColor}
      message={survey.endMessage || (language === 'es' ? 'Gracias.' : 'Thank you.')}
      onExit={kioskAllDone ? onFinishAll! : onComplete}
      exitLabel={
        kioskAllDone
          ? language === 'es'
            ? 'Listo'
            : 'Done'
          : language === 'es'
            ? 'Continuar'
            : 'Continue'
      }
    />
  );
};

export default StepSurvey;
